import { ChangeDetectionStrategy, Component, ChangeDetectorRef, Input, OnDestroy } from '@angular/core';
import {
    CertificatesApiService,
    CertificatesApiServiceObservable,
    GetSigningJobStatusResponse,
    InitSigningJobResponse,
} from '@kempoweroyj/ngx-certificates-api-client';
import { BehaviorSubject, Subject, interval, Observable } from 'rxjs';
import { map, distinctUntilChanged, takeUntil, startWith, switchMap, filter } from 'rxjs/operators';
import { FileUploader, FileItem } from 'ng2-file-upload';
import { PopupService } from 'shared/popup-service/popup.service';
import { AuthService } from 'providers/auth.service';

const CERTIFICATE_SIGNING_POLLING_INTERVAL_MS = 5000; // 5s
const CERTIFICATE_SIGNING_LOCAL_STORAGE_PREFIX = 'ce-certificate-signing-jobs';

interface SigningJob extends Partial<GetSigningJobStatusResponse> {
    jobId?: string;
    /** pkg name for ui identification */
    fileName: string;
    initStatus?: 'initializing' | 'initialized' | 'failed';
    uploadStatus?: 'uploading' | 'uploaded' | 'failed';
    statusTimestamp: string;
}

@Component({
    selector: 'ce-certificate-signing',
    templateUrl: './certificate-signing.component.html',
    styleUrls: ['./certificate-signing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificateSigningComponent implements OnDestroy {
    @Input() stationId: string;
    @Input() serialNumber: string;

    // Collection of jobs
    private jobsStore = new Map<string, SigningJob>(); // key = jobId OR tempId
    jobs$ = new BehaviorSubject<SigningJob[]>([]);

    // Selected job id
    private selectedJobId$ = new BehaviorSubject<string | null>(null);

    // Expose currently selected job as observable
    selectedJob$: Observable<SigningJob | undefined> = this.selectedJobId$.pipe(
        switchMap((id) =>
            this.jobs$.pipe(
                map((list) => list.find((j) => (j.jobId || this.tempId(j.fileName)) === id)),
                distinctUntilChanged((a, b) => a === b || (!!a && !!b && a.statusTimestamp === b.statusTimestamp))
            )
        )
    );

    // Ongoing jobs
    ongoingJobs$: Observable<SigningJob[]> = this.jobs$.pipe(
        map((list) => list.filter((j) => !this.isTerminal(j))),
        map((list) => list.sort((a, b) => _sort(a, b, 'desc'))) // newest first
    );
    // Completed jobs
    historyJobs$: Observable<SigningJob[]> = this.jobs$.pipe(
        map((list) => list.filter((j) => this.isTerminal(j))),
        map((list) => list.sort((a, b) => _sort(a, b, 'desc'))) // newest first
    );

    // Polling control
    private pollingActiveFor = new Set<string>();
    private destroy$ = new Subject<void>();

    // File uploader
    uploader: FileUploader;
    readonly MAX_FILE_SIZE = 1 * (1024 * 1024 * 1024);
    readonly QUEUE_LIMIT = 4;
    readonly ALLOWED_FILE_TYPES = ['application/gzip', 'application/x-gzip'];

    // Generic UI elements
    showRawJson = false;
    showCertReqTree = false;

    constructor(
        private certificatesApi: CertificatesApiService,
        private certificatesApi$: CertificatesApiServiceObservable,
        private authService: AuthService,
        private cdRef: ChangeDetectorRef,
        private popupService: PopupService
    ) {
        this.uploader = new FileUploader({
            url: '',
            autoUpload: false,
            disableMultipart: true, // shredding the file changes the checksum
            removeAfterUpload: true,
            queueLimit: this.QUEUE_LIMIT,
            maxFileSize: this.MAX_FILE_SIZE,
            allowedMimeType: this.ALLOWED_FILE_TYPES, // ng2-file-upload does not support generic file type gzip
            method: 'PUT',
            headers: [{ name: 'Content-Type', value: 'application/gzip' }],
        });
        this.bindUploaderCallbacks();
    }

    // ------------- Component lifecycle -------------
    ngOnInit() {
        if (!this.stationId) {
            return; // stop bootstrap if no stationId
        }

        // Try populating existing jobs from local storage
        this.loadFromLocalStorage();

        // Emit initial jobs
        this.emitJobs();

        // Resume polling
        for (const job of this.jobsStore.values()) {
            this.ensurePolling(job.jobId); // ensurePolling will check if job should be polled
        }
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.pollingActiveFor.clear();
    }

    // ------------- Public API -------------

    selectJob(jobId: string) {
        this.selectedJobId$.next(jobId);
        const job = this.getJob(jobId);
        if (job && job.uploadStatus === 'uploaded' && !this.isTerminal(job)) {
            this.ensurePolling(jobId);
        }
    }

    refreshSelected() {
        const id = this.selectedJobId$.value;
        if (id) this.getJobStatus(id);
    }

    selectFileWithInput(files: File[]) {
        this.uploader.addToQueue(files);
    }
    selectFileWithDrop(files: File[]) {
        this.uploader.addToQueue(files);
    }

    job$(jobId: string): Observable<SigningJob | undefined> {
        return this.jobs$.pipe(
            map((list) => list.find((j) => j.jobId === jobId)),
            distinctUntilChanged((a, b) => a?.statusTimestamp === b?.statusTimestamp)
        );
    }

    trackByJobId = (_: number, job: SigningJob | any) => job.jobId || this.tempId(job.fileName);
    trackByRequestId = (_: number, req: any) => req.requestId;

    toggleRawJson() {
        this.showRawJson = !this.showRawJson;
        this.cdRef.markForCheck();
    }

    toggleCertReqTree() {
        this.showCertReqTree = !this.showCertReqTree;
        this.cdRef.markForCheck();
    }

    openDownload(url: string) {
        window.open(url, '_blank');
    }
    isJobSelected(jobId: string) {
        return this.selectedJobId$.value === jobId;
    }

    // ------------- Core logic -------------

    private tempId(fileName: string) {
        return `temp-${fileName}`;
    }

    private upsert(key: string, patch: Partial<SigningJob>) {
        const existing = this.jobsStore.get(key);

        let merged: SigningJob;
        if (existing) {
            merged = {
                ...existing,
            };
        } else {
            const fileName = patch.fileName || key.replace(/^temp-/, '');

            merged = {
                fileName,
                jobId: fileName,
            } as SigningJob;
        }

        merged = {
            ...merged,
            ...patch,
            statusTimestamp: new Date().toISOString(),
        };

        this.jobsStore.set(key, merged);
        this.emitJobs();
    }

    private replaceTempIdWithJobId(tempKey: string, jobId: string) {
        const tempJob = this.jobsStore.get(tempKey);
        if (!tempJob) return;

        this.jobsStore.delete(tempKey);
        this.jobsStore.set(jobId, { ...tempJob, jobId });

        if (this.isJobSelected(tempKey)) {
            this.selectedJobId$.next(jobId); // update selected with new jobId
        }

        this.emitJobs();
    }

    private emitJobs() {
        this.jobs$.next(Array.from(this.jobsStore.values())); // update observable
        this.updateLocalStorage(); // persist

        this.cdRef.markForCheck();
    }

    private getJob(key: string) {
        return this.jobsStore.get(key);
    }

    private isTerminal(job: SigningJob | GetSigningJobStatusResponse | any): boolean {
        const initStatus = job?.initStatus;
        const uploadStatus = job?.uploadStatus;
        const status = job?.status;

        if (initStatus === 'failed' || uploadStatus === 'failed') return true;
        if (status === 'failed' || status === 'completed') return true;

        return false;
    }

    // ------------- Polling -------------

    private ensurePolling(jobId: string) {
        if (this.pollingActiveFor.has(jobId)) return;

        const job = this.getJob(jobId);
        if (!job || job.uploadStatus !== 'uploaded' || this.isTerminal(job)) return;

        this.pollingActiveFor.add(jobId);

        interval(CERTIFICATE_SIGNING_POLLING_INTERVAL_MS)
            .pipe(
                startWith(0),
                takeUntil(this.destroy$),
                filter(() => this.pollingActiveFor.has(jobId)),
                switchMap(() => this.certificatesApi$.getSigningJobStatus(jobId))
            )
            .subscribe({
                next: (res) => {
                    // Update job status
                    this.upsert(jobId, res);

                    // Stop polling if job reached terminal state
                    if (this.isTerminal(res)) {
                        this.pollingActiveFor.delete(jobId);
                    }
                },
                error: () => {
                    // keep polling; transient errors ignored
                },
            });
    }

    // ------------- Job lifecycle -------------

    private async initJob(item: FileItem) {
        const fileName = item.file.name;
        const tempKey = this.tempId(fileName);

        // Create job entry
        this.upsert(tempKey, {
            fileName,
            initStatus: 'initializing',
            uploadStatus: undefined,
        });

        // Request upload URL
        let initRes: InitSigningJobResponse;
        try {
            initRes = await this.certificatesApi.initSigningJob({
                stationId: this.stationId,
                fileName,
                serialNumber: this.serialNumber,
            });
        } catch {
            this.upsert(tempKey, { initStatus: 'failed' });
            return;
        }

        item.url = initRes.uploadUrl; // update upload url
        //item.url = 'error'; // update upload url
        (item as any).jobId = initRes.jobId; // attach jobId to item for later reference

        // Start upload
        this.replaceTempIdWithJobId(tempKey, initRes.jobId);
        this.upsert(initRes.jobId, {
            jobId: initRes.jobId,
            fileName,
            initStatus: 'initialized',
        });

        this.uploader.uploadItem(item);
    }

    async getJobStatus(jobId: string) {
        try {
            const res = await this.certificatesApi.getSigningJobStatus(jobId);
            this.upsert(jobId, res);

            if (this.isTerminal(res)) {
                this.pollingActiveFor.delete(jobId);
            } else {
                this.ensurePolling(jobId);
            }
        } catch {
            // ignore manual refresh errors
        }
    }

    async startSigning(jobId: string) {
        try {
            await this.certificatesApi.startSigningJob({ jobId });
            this.ensurePolling(jobId);
        } catch {
            this.popupService.error('Error starting signing job');
        }
    }

    // ------------- Local Storage Persist -------------

    private localStorageKey() {
        const profile = this.authService.profile;
        const uid = `${profile.tenantId}-${this.stationId}`; // tenant & station specific (separate tenant data)

        return `${CERTIFICATE_SIGNING_LOCAL_STORAGE_PREFIX}-${uid}`;
    }
    private updateLocalStorage() {
        const data = JSON.stringify(Array.from(this.jobsStore.entries()));

        localStorage.setItem(this.localStorageKey(), data);
    }
    private loadFromLocalStorage() {
        try {
            const data = localStorage.getItem(this.localStorageKey());

            if (!data) return;

            const jobs: [string, SigningJob][] = JSON.parse(data);

            // Populate store
            this.jobsStore = new Map(jobs);
        } catch (error) {
            console.error('Error loading signing jobs from local storage', error);
        }
    }
    static clearLocalStorage() {
        const removeKeys: string[] = [];

        // Loop through localStorage keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (key && key.startsWith(CERTIFICATE_SIGNING_LOCAL_STORAGE_PREFIX)) {
                removeKeys.push(key);
            }
        }

        // Remove
        removeKeys.forEach((k) => localStorage.removeItem(k));
    }

    // ------------- Uploader callbacks -------------

    private bindUploaderCallbacks() {
        this.uploader.onAfterAddingFile = async (item: FileItem) => {
            await this.initJob(item);
        };

        this.uploader.onWhenAddingFileFailed = (item) => {
            if (item.size >= this.MAX_FILE_SIZE) {
                this.popupService.error(
                    `File too big (${item.size}). Max ${this.MAX_FILE_SIZE / (1024 * 1024 * 1024)}GB`
                );
            } else if (this.uploader.queue.length === this.QUEUE_LIMIT) {
                this.popupService.error(
                    `Queue limit exceeded (${this.uploader.queue.length}). Max: ${this.QUEUE_LIMIT}`
                );
            } else if (!this.ALLOWED_FILE_TYPES.includes(item.type)) {
                this.popupService.error(
                    `Invalid file type (${item.type || 'unknown'}). Allowed: ${this.ALLOWED_FILE_TYPES.join(', ')}`
                );
            }
        };

        this.uploader.onBeforeUploadItem = (item) => {
            item.withCredentials = false;
            const jobId = (item as any).jobId || this.tempId(item.file.name);
            this.upsert(jobId, { uploadStatus: 'uploading' });
        };

        this.uploader.onProgressItem = () => {
            this.cdRef.markForCheck();
        };

        this.uploader.onErrorItem = (item) => {
            const jobId = (item as any).jobId || this.tempId(item.file.name);
            this.upsert(jobId, { uploadStatus: 'failed' });
            const job = this.getJob(jobId);
            this.popupService.error(`Error uploading ${job?.fileName || ''}`);
        };

        this.uploader.onSuccessItem = async (item) => {
            const jobId = (item as any).jobId;
            if (!jobId) return;
            this.upsert(jobId, { uploadStatus: 'uploaded' });

            // Select the job if not already selected
            if (!this.selectedJobId$.value) {
                this.selectJob(jobId);
            }

            // Sleep 1s to allow backend to process the upload
            await _sleep(1000);

            await this.startSigning(jobId);
            this.ensurePolling(jobId);
        };
    }
}

function _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function _sort(a: SigningJob, b: SigningJob, order: 'asc' | 'desc' = 'desc'): 0 | 1 | -1 {
    const fallback = order === 'desc' ? Infinity : -Infinity; // invalid at the end of the list
    const ta = a.createdAt || a.completedAt || a.statusTimestamp || fallback;
    const tb = b.createdAt || b.completedAt || b.statusTimestamp || fallback;

    if (ta > tb) return order === 'desc' ? -1 : 1;
    if (ta < tb) return order === 'desc' ? 1 : -1;

    return 0;
}
